/**
 * Channel Plugin Contract
 *
 * Cada canal (WhatsApp Cloud API, WhatsApp WAHA, Instagram, etc.) implementa
 * este contrato para plugar na aba Canais. Adicionar um novo canal é só
 * criar um plugin que respeite esta interface e registrá-lo no registry.
 *
 * Veja channels/plugins/_TEMPLATE/ para esqueleto.
 *
 * Estrutura esperada de um plugin:
 *
 * {
 *   // Metadata estática
 *   id: string,                          // 'whatsapp_cloud_api'
 *   name: string,                        // 'WhatsApp Oficial'
 *   description: string,
 *   icon: ReactComponent,                // Tabler icon
 *   color: string,                       // brand
 *   gradient: string,
 *   category: 'messaging' | 'social' | 'web' | 'email',
 *   isAvailable: boolean,                // false → "Em breve"
 *   provider: string,                    // valor do AgentFlow.whatsapp_provider
 *   conflictsWith?: string[],            // ids de canais mutuamente exclusivos
 *
 *   // Hook de status (consumido pelo card)
 *   useStatus: (agentId) => {
 *     isConnected: boolean,
 *     isLoading: boolean,
 *     statusLabel: string,
 *     statusVariant: 'success' | 'warning' | 'danger' | 'info' | 'neutral',
 *     summary?: string,
 *     hasAlert?: boolean,
 *     alertSeverity?: 'warning' | 'error',
 *     refresh: () => void,
 *     raw?: object,                      // dados brutos para o detail view
 *   },
 *
 *   // Renders
 *   renderCard: (agentId) => ReactElement,
 *   renderDetail: (agentId, navigation) => ReactElement,
 *   renderConnectFlow?: (agentId, onConnected, onCancel) => ReactElement,
 * }
 *
 * Convenções:
 * - useStatus DEVE ser estável (mesmo objeto entre renders se nada mudou)
 * - renderCard recebe contexto de status já carregado pelo grid
 * - renderDetail recebe `navigation` com { goBackToGrid, switchToChannel }
 * - Hooks devem usar React.useEffect/useState normais — sem dependência externa
 */

export const CHANNEL_CATEGORIES = {
  MESSAGING: 'messaging',
  SOCIAL: 'social',
  WEB: 'web',
  EMAIL: 'email',
};

export const STATUS_VARIANTS = {
  SUCCESS: 'success',
  WARNING: 'warning',
  DANGER: 'danger',
  INFO: 'info',
  NEUTRAL: 'neutral',
};

/**
 * Helper para validar plugin no registry. Lança erro descritivo em dev.
 */
export function validateChannelPlugin(plugin) {
  const required = ['id', 'name', 'description', 'icon', 'color', 'isAvailable'];
  const missing = required.filter((k) => plugin[k] === undefined);
  if (missing.length > 0) {
    throw new Error(
      `[ChannelPlugin] plugin "${plugin?.id || '<sem id>'}" sem campos obrigatórios: ${missing.join(', ')}`,
    );
  }
  if (plugin.isAvailable && typeof plugin.useStatus !== 'function') {
    throw new Error(
      `[ChannelPlugin] plugin "${plugin.id}" disponível precisa de useStatus()`,
    );
  }
  if (plugin.isAvailable && typeof plugin.renderDetail !== 'function') {
    throw new Error(
      `[ChannelPlugin] plugin "${plugin.id}" disponível precisa de renderDetail()`,
    );
  }
  return plugin;
}
